import { useForm } from "@inertiajs/react";
import {
  Children,
  cloneElement,
  type FormEvent,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useState,
} from "react";

import Button from "@/components/Button";
import LinkButton from "@/components/LinkButton";
import SearchableSelect from "@/components/SearchableSelect";
import { useGenderedText } from "@/lib/genderedText";
import { usePermissions } from "@/lib/permissions";
import { courses, users } from "@/routes";
import type { User } from "@/types";

const USER_CATEGORY_LABELS: Record<string, string> = {
  senora: "Señora",
  universitaria: "Universitaria",
  numeraria_auxiliar: "Numeraria Auxiliar",
  agregada: "Agregada",
  numeraria: "Numeraria",
  la_cuesta: "S V",
  la_cuesta_sn: "S SV",
  la_cuesta_cp: "CP SV",
};

const ROLE_FIELDS = [
  { name: "is_admin", key: "isAdmin" },
  { name: "is_house_manager", key: "isHouseManager" },
  { name: "is_house_master", key: "isHouseMaster" },
  { name: "is_director", key: "isDirector" },
  { name: "is_subscriber", key: "isSubscriber" },
  { name: "is_center_director", key: "isCenterDirector" },
  { name: "is_student", key: "isStudent" },
] as const;

const FIELD_INPUT_CLASSES =
  "w-full border-0 bg-transparent p-0 font-sans text-base font-light text-ink shadow-none placeholder:text-ink/40 focus:outline-none focus:ring-0 focus:shadow-none";

function Section({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="flex flex-col gap-4 rounded-2xl border-2 border-ink/20 p-5">
      <legend className="flex items-center gap-2 px-2 font-sans text-sm font-semibold text-ink">
        <span className="text-steel">{icon}</span>
        {title}
      </legend>
      {children}
    </fieldset>
  );
}

/** Labels a single input/select the same way `Section` labels a card: the
 * name sits on the border as a legend once the field has a value, so a
 * filled field stays identifiable. While empty, only the placeholder (also
 * derived from `label`) shows, so the two don't display redundantly. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  const child = Children.only(children);
  const value = isValidElement(child)
    ? (child.props as { value?: unknown }).value
    : undefined;
  const hasValue = value !== undefined && value !== null && value !== "";

  const content = isValidElement(child)
    ? cloneElement(child as ReactElement<{ placeholder?: string }>, {
        placeholder:
          (child.props as { placeholder?: string }).placeholder ?? label,
      })
    : child;

  return (
    <fieldset
      className={`min-w-0 rounded-full border-2 border-ink px-4 focus-within:border-steel ${hasValue ? "pb-2.5" : "py-3.5"}`}
    >
      {hasValue && (
        <legend className="truncate px-1.5 font-sans text-xs text-ink/60">
          {label}
        </legend>
      )}
      {content}
    </fieldset>
  );
}

function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={FIELD_INPUT_CLASSES} {...props} />;
}

function PasswordInput(
  props: Omit<InputHTMLAttributes<HTMLInputElement>, "type">,
) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <Input type={visible ? "text" : "password"} {...props} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="flex shrink-0 items-center text-ink/60 transition-colors hover:text-ink"
        aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
        aria-pressed={visible}
        tabIndex={-1}
      >
        {visible ? <EyeIcon /> : <EyeOffIcon />}
      </button>
    </div>
  );
}

type Props = {
  data: User;
  centers: { id: number; name: string }[];
};

export default function UsersEdit({ data: user, centers }: Props) {
  const t = useGenderedText();
  const { canManageUsersNav } = usePermissions();
  // Mirrors the controller's post-update redirect: admins land back on the
  // user list, everyone else (self-editing their profile) on the courses list.
  const cancelUrl = canManageUsersNav ? users.index().url : courses.index().url;

  const form = useForm({
    first_name: user.first_name ?? "",
    last_name: user.last_name ?? "",
    second_last_name: user.second_last_name ?? "",
    rut: user.rut ?? "",
    birth_date: user.birth_date ?? "",
    email: user.email ?? "",
    phone_number: user.phone_number ?? "",
    address: user.address ?? "",
    commune: user.commune ?? "",
    city: user.city ?? "",
    center_id: user.center_id ? String(user.center_id) : "",
    user_category: user.user_category ?? "",
    password: "",
    password_confirmation: "",
    is_admin: user.is_admin ?? false,
    is_house_manager: user.is_house_manager ?? false,
    is_house_master: user.is_house_master ?? false,
    is_director: user.is_director ?? false,
    is_subscriber: user.is_subscriber ?? false,
    is_center_director: user.is_center_director ?? false,
    is_student: user.is_student ?? false,
    is_cooperator: user.is_cooperator ?? false,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    form.transform((data) => ({
      user: { ...data, center_id: data.center_id || null },
    }));
    form.patch(users.update(user.id).url);
  }

  return (
    <div className="mx-auto w-[90%] max-w-2xl py-10">
      <h1 className="mb-6 font-sans text-3xl font-semibold text-ink">
        {(user.full_name as string | null) || t("usuarios")}
      </h1>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <Section icon={<PersonIcon />} title="Datos personales">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nombre">
              <Input
                value={form.data.first_name}
                onChange={(e) => form.setData("first_name", e.target.value)}
                required
              />
            </Field>
            <Field label="Apellido Paterno">
              <Input
                value={form.data.last_name}
                onChange={(e) => form.setData("last_name", e.target.value)}
                required
              />
            </Field>
            <Field label="Apellido Materno">
              <Input
                value={form.data.second_last_name}
                onChange={(e) =>
                  form.setData("second_last_name", e.target.value)
                }
              />
            </Field>
            <Field label="RUT">
              <Input
                value={form.data.rut}
                onChange={(e) => form.setData("rut", e.target.value)}
                required
              />
            </Field>
            <Field label="Fecha de nacimiento">
              <Input
                type="date"
                value={form.data.birth_date}
                onChange={(e) => form.setData("birth_date", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={<EmailIcon />} title="Contacto">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Email">
              <Input
                type="email"
                value={form.data.email}
                onChange={(e) => form.setData("email", e.target.value)}
              />
            </Field>
            <Field label="Teléfono">
              <Input
                value={form.data.phone_number}
                onChange={(e) => form.setData("phone_number", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={<MapPinIcon />} title="Dirección">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Dirección">
              <Input
                value={form.data.address}
                onChange={(e) => form.setData("address", e.target.value)}
              />
            </Field>
            <Field label="Comuna">
              <Input
                value={form.data.commune}
                onChange={(e) => form.setData("commune", e.target.value)}
              />
            </Field>
            <Field label="Ciudad">
              <Input
                value={form.data.city}
                onChange={(e) => form.setData("city", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={<BuildingIcon />} title="Organización">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Centro">
              <SearchableSelect
                value={form.data.center_id}
                onChange={(v) => form.setData("center_id", v)}
                options={[
                  { value: "", label: "Sin centro" },
                  ...centers.map((c) => ({
                    value: String(c.id),
                    label: c.name,
                  })),
                ]}
              />
            </Field>

            <Field label="Categoría">
              <SearchableSelect
                value={form.data.user_category}
                onChange={(v) => form.setData("user_category", v)}
                options={[
                  { value: "", label: "Sin categoría" },
                  ...Object.entries(USER_CATEGORY_LABELS).map(
                    ([value, label]) => ({ value, label }),
                  ),
                ]}
              />
            </Field>
          </div>
        </Section>

        <Section icon={<LockIcon />} title="Contraseña">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Nueva contraseña (opcional)">
              <PasswordInput
                autoComplete="new-password"
                value={form.data.password}
                onChange={(e) => form.setData("password", e.target.value)}
              />
            </Field>
            <Field label="Confirmar contraseña">
              <PasswordInput
                autoComplete="new-password"
                value={form.data.password_confirmation}
                onChange={(e) =>
                  form.setData("password_confirmation", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        <Section icon={<ShieldIcon />} title="Roles">
          <div className="flex flex-col gap-2">
            {ROLE_FIELDS.map(({ name, key }) => (
              <label
                key={name}
                className="flex w-fit cursor-pointer items-center gap-2 self-start font-sans text-sm text-ink"
              >
                <input
                  type="checkbox"
                  checked={form.data[name]}
                  onChange={(e) => form.setData(name, e.target.checked)}
                  className="h-4 w-4 rounded-full border-ink/50 text-steel focus:ring-steel/40"
                />
                <span>{t(key)}</span>
              </label>
            ))}
          </div>
        </Section>

        <div className="mt-2 flex gap-3">
          <Button type="submit" disabled={form.processing}>
            Guardar
          </Button>
          <LinkButton href={cancelUrl}>Cancelar</LinkButton>
        </div>
      </form>
    </div>
  );
}

function PersonIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="3.5" />
      <path strokeLinecap="round" d="M4.5 20a7.5 7.5 0 0115 0" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.5 6l8.5 7 8.5-7"
      />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21s7-6.1 7-11.5A7 7 0 105 9.5C5 14.9 12 21 12 21z"
      />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="4" y="3" width="12" height="18" rx="1" />
      <path strokeLinecap="round" d="M8 7h4M8 11h4M8 15h4M16 10h4v11h-4" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path strokeLinecap="round" d="M8 10.5V7a4 4 0 018 0v3.5" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3l7 3v5c0 4.6-3 8.4-7 10-4-1.6-7-5.4-7-10V6l7-3z"
      />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12s3.75-7.5 9.75-7.5 9.75 7.5 9.75 7.5-3.75 7.5-9.75 7.5S2.25 12 2.25 12Z"
      />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.98 8.22A10.48 10.48 0 0 0 2.25 12s3.75 7.5 9.75 7.5c1.66 0 3.18-.4 4.52-1.05M6.6 6.6A9.9 9.9 0 0 1 12 4.5c6 0 9.75 7.5 9.75 7.5a13.2 13.2 0 0 1-2.16 3.02M3 3l18 18M9.9 9.9a3 3 0 0 0 4.2 4.2"
      />
    </svg>
  );
}
